import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "../../utils/hooks/useAxios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { downloadFile, filterObject } from "@/utils/helpers";
import { MIME_TYPES } from "@/utils/constants";
import { SYSTEMS } from "@/utils/permissions";
import { useSystem } from "@/utils/hooks/useLocation";

const incidentReportsBase = (system = "makkah") => {
  const systemName = system === "makkah" ? "makkah" : "madina";
  return `${systemName}/incident-reports`;
};

// ******* Search Pilgrim *******
/**
 * GET {system}/incident-reports/search-pilgrim?{key}={value}
 * Supported keys: pilgrim_id, passport_number, visa_number, nusuk_card_id, border_number
 */
export const useSearchPilgrimMutation = (options = {}) => {
  const { system = "makkah" } = options;
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ key, value }) =>
      axiosProtected.get(`${incidentReportsBase(system)}/search-pilgrim`, {
        params: { [key]: value },
      }),
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("SEARCH_FAILED"));
    },
  });
};

// ******* Incident Reports (Reports & Incidents list) *******
/**
 * GET {system}/incident-reports - paginated list with search and filters
 */
export const useIncidentReportsQuery = (params = {}, options = {}) => {
  const { system = "makkah" } = options;
  const { axiosProtected } = useAxios();
  const filteredParams = filterObject(params);
  const systemName = system === "makkah" ? "makkah" : "madina";
  return useQuery({
    queryKey: ["incident-reports", system, filteredParams],
    queryFn: () => axiosProtected.get(`${systemName}/incident-reports`, { params: filteredParams }),
  });
};

/**
 * GET {system}/incident-reports/report-with-house - paginated list of reports with house (buildings not inspected)
 */
export const useReportWithHouseQuery = (params = {}, options = {}) => {
  const { system = "makkah" } = options;
  const { axiosProtected } = useAxios();
  const filteredParams = filterObject(params);
  return useQuery({
    queryKey: ["incident-reports", "report-with-house", system, filteredParams],
    queryFn: () =>
      axiosProtected.get(`${incidentReportsBase(system)}/report-with-house`, {
        params: filteredParams,
      }),
  });
};

/**
 * GET {system}/incident-reports/:id - get one incident report for view/edit
 */
export const useGetIncidentReportQuery = (id, options = {}) => {
  const { system = "makkah" } = options;
  const { axiosProtected } = useAxios();
  return useQuery({
    queryKey: ["incident-reports", system, id],
    queryFn: () => axiosProtected.get(`${incidentReportsBase(system)}/${id}`),
    enabled: Boolean(id),
    staleTime: 60 * 1000, // 1 min – avoids duplicate fetch on Strict Mode double mount
  });
};

/**
 * PUT {system}/incident-reports/:id - update incident report (with optional file attachments)
 */
export const useUpdateIncidentReportMutation = (options = {}) => {
  const { system = "makkah" } = options;
  const { axiosProtected } = useAxios();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const base = incidentReportsBase(system);

  return useMutation({
    mutationFn: ({ id, ...payload }) => {
      const hasFiles =
        Array.isArray(payload.documents) &&
        payload.documents.length > 0 &&
        payload.documents.some((f) => f instanceof File);
      if (hasFiles) {
        const formData = new FormData();
        const { documents, ...rest } = payload;
        Object.entries(rest).forEach(([key, value]) => {
          if (value == null || value === "") return;
          if (Array.isArray(value) || (typeof value === "object" && !(value instanceof File))) {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value);
          }
        });
        payload.documents
          .filter((f) => f instanceof File)
          .forEach((file) => formData.append("documents[]", file));
        return axiosProtected.post(`${base}/${id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      const { documents, ...body } = payload;
      return axiosProtected.post(`${base}/${id}`, body);
    },
    onSuccess: (_, variables) => {
      // Invalidate only list queries; skip single-report query to avoid extra GET after submit (we navigate away)
      queryClient.invalidateQueries({
        queryKey: ["incident-reports", system],
        predicate: (query) => query.queryKey[2] !== variables.id,
      });
      queryClient.invalidateQueries({
        queryKey: ["incident-reports", "report-with-house", system],
      });
      toast.success(t("REPORT_UPDATED_SUCCESSFULLY") || "Report updated successfully");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("REPORT_UPDATE_FAILED") || "Failed to update report");
    },
  });
};

/**
 * DELETE {system}/incident-reports/:id/attachments/:index - delete one attachment from a report
 */
export const useDeleteIncidentReportAttachmentMutation = (options = {}) => {
  const { system = "makkah" } = options;
  const { axiosProtected } = useAxios();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const base = incidentReportsBase(system);

  return useMutation({
    mutationFn: ({ reportId, attachmentIndex }) =>
      axiosProtected.delete(`${base}/${reportId}/attachments/${attachmentIndex}`),
    onSuccess: (_, { reportId }) => {
      queryClient.invalidateQueries({ queryKey: ["incident-reports", system, reportId] });
      toast.success(t("ATTACHMENT_DELETED_SUCCESSFULLY") || "Attachment deleted successfully");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("ATTACHMENT_DELETE_FAILED") || "Failed to delete attachment");
    },
  });
};

/**
 * DELETE {system}/incident-reports/:id - delete incident report
 */
export const useDeleteIncidentReportMutation = (options = {}) => {
  const { system = "makkah" } = options;
  const { axiosProtected } = useAxios();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const base = incidentReportsBase(system);
  return useMutation({
    mutationFn: (id) => axiosProtected.delete(`${base}/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["incident-reports", system] });
      queryClient.removeQueries({ queryKey: ["incident-reports", system, id] });
      toast.success(t("REPORT_DELETED_SUCCESSFULLY") || "Report deleted successfully");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("REPORT_DELETE_FAILED") || "Failed to delete report");
    },
  });
};

/**
 * POST {system}/incident-reports/:id/resolve — recipient + reporting entity (report authorities from lookups).
 */
export const useResolveIncidentReportMutation = (options = {}) => {
  const { system = "makkah" } = options;
  const { axiosProtected } = useAxios();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const base = incidentReportsBase(system);

  return useMutation({
    mutationFn: ({ id, report_authority_id, reported_person_name }) =>
      axiosProtected.post(
        `${base}/${id}/resolve`,
        filterObject({ report_authority_id, reported_person_name })
      ),
    onSuccess: () => {
      toast.success(t("CHANGES_SAVED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["incident-reports", system] });
      queryClient.invalidateQueries({ queryKey: ["nusuk-incident-reports"] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("SOMETHING_WENT_WRONG"));
    },
  });
};

/**
 * GET {system}/incident-reports/lookups - case types, document types, card statuses, body locations, report authorities, case authorities
 */
export const useIncidentReportLookupsQuery = (options = {}) => {
  const { system = "makkah" } = options;
  const { axiosProtected } = useAxios();
  return useQuery({
    queryKey: ["incident-reports", "lookups", system],
    queryFn: () => axiosProtected.get(`incident-reports/lookups`),
  });
};

/**
 * GET makkah/incident-reports/report-creators - list of report creators for filter dropdown
 */
export const useReportCreatorsQuery = (options = {}) => {
  const { system = "makkah" } = options;
  const { axiosProtected } = useAxios();
  let systemName = system === "makkah" ? "makkah" : "madina";
  return useQuery({
    queryKey: ["incident-reports", "creators", system],
    queryFn: () => axiosProtected.get(`${systemName}/incident-reports/creators`),
  });
};

/**
 * POST {system}/incident-reports - create incident report (with optional file attachments)
 */
export const useCreateIncidentReportMutation = (options = {}) => {
  const { system = "makkah" } = options;
  const { axiosProtected } = useAxios();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const base = incidentReportsBase(system);

  return useMutation({
    mutationFn: (payload) => {
      const hasFiles =
        Array.isArray(payload.documents) &&
        payload.documents.length > 0;
      if (hasFiles) {
        const formData = new FormData();
        const { documents, ...rest } = payload;
        Object.entries(rest).forEach(([key, value]) => {
          if (value == null || value === "") return;
          if (Array.isArray(value) || (typeof value === "object" && !(value instanceof File))) {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value);
          }
        });
        payload.documents.forEach((file) => formData.append("documents[]", file));
        return axiosProtected.post(base, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      const { documents, ...body } = payload;
      return axiosProtected.post(base, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["incident-reports", system] });
      toast.success(t("REPORT_CREATED_SUCCESSFULLY") || "Report created successfully");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("REPORT_CREATE_FAILED") || "Failed to create report");
    },
  });
};

// ******* Campaign Reports *******
/**
 * Mutation for getting campaign reports in JSON format
 * Endpoint: POST makkah/reports/arrival-campaign-segments
 */
export const useCampaignReportMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useMutation({
    mutationFn: async (payload) => {
      // Extract page and limit for query params (only for JSON format)
      const { page, per_page, ...rest } = payload;

      // Filter out empty values
      const filteredPayload = filterObject(rest);

      // For JSON format, send page and per_page as query params
      const config =
        payload.format === "json" && (page || per_page)
          ? {
            params: {
              ...(page && { page }),
              ...(per_page && { per_page }),
            },
          }
          : undefined;

        return axiosProtected.post(
          `${system}/reports/arrival-campaign-segments`,
          filteredPayload,
          config
        );
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message ||
        t("REPORT_GENERATION_FAILED") ||
        "Failed to generate report"
      );
    },
  });
};

/**
 * Query for exporting campaign reports (PDF/Excel)
 * Only enabled when format is pdf or excel and shouldDownload is true
 */
export const useCampaignReportQuery = (payload, enabled) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useQuery({
    queryKey: ["campaign-report-export", payload],
    queryFn: async () => {
      // Filter out empty values
      const { orientation, ...rest } = payload;
      const filteredPayload = filterObject(rest);

      const mapFormat = {
        pdf: "pdf",
        excel: "xlsx",
      };

      const format = mapFormat[filteredPayload.format] || "pdf";

      // For PDF, expect HTML response; for Excel, expect blob
        const response = await axiosProtected.post(
          `${system}/reports/arrival-campaign-segments`,
          filteredPayload,
          {
          responseType: "blob",
          params:
            filteredPayload.format === "pdf"
              ? {
                orientation: orientation || "portrait",
              }
              : undefined,
        }
      );

      const filename = `campaign-report.${format}`;

      // Handle blob response for Excel
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      downloadFile(blob, filename);

      return response;
    },
    enabled:
      enabled && (payload?.format === "pdf" || payload?.format === "excel"),
  });
};

// ******* Group Reports *******

/**
 * Mutation for getting group reports in JSON format
 * Endpoint: POST makkah/reports/group-details
 */
export const useGroupReportMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useMutation({
    mutationFn: async (payload) => {
      // Extract page and per_page for query params (only for JSON format)
      const { page, per_page, ...rest } = payload;

      // Filter out empty values
      const filteredPayload = filterObject(rest);
      // >><<
      // For JSON format, send page and per_page as query params
      const config =
        payload.format === "json" && (page || per_page)
          ? {
            params: {
              ...(page && { page }),
              ...(per_page && { per_page }),
            },
          }
          : undefined;

        return axiosProtected.post(
          `${system}/reports/group-details`,
          filteredPayload,
          config
        );
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message ||
        t("REPORT_GENERATION_FAILED") ||
        "Failed to generate report"
      );
    },
  });
};

/**
 * Query for exporting group reports (PDF/Excel)
 * Only enabled when format is pdf or excel and shouldDownload is true
 */
export const useGroupReportQuery = (payload, enabled) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useQuery({
    queryKey: ["group-report-export", payload],
    queryFn: async () => {
      // // Filter out empty values
      // const { orientation, ...rest } = payload;
      // const filteredPayload = filterObject(rest);
      const { orientation, ...rest } = payload;

      const mapFormat = {
        pdf: "pdf",
        excel: "xlsx",
      };

      // Filter out empty values
      const filteredPayload = filterObject(rest);

      const format = mapFormat[filteredPayload.format] || "pdf";

      // For PDF, expect HTML response; for Excel, expect blob

        const response = await axiosProtected.post(
          `${system}/reports/group-details`,
          filteredPayload,
          {
          responseType: "blob",
          params:
            filteredPayload.format === "pdf"
              ? {
                orientation: orientation || "portrait",
              }
              : undefined,
        }
      );

      const filename = `group-report.${format}`;

      // Handle blob response for Excel
      const blob = new Blob([response.data], {
        type: format === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      downloadFile(blob, filename);

      return response;
    },
    enabled:
      enabled && (payload?.format === "pdf" || payload?.format === "excel"),
  });
};

// ******* Campaign Statement Reports *******

export const useCampaignStatementReportMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useMutation({
    mutationFn: async (payload) => {
      const { page, per_page, ...rest } = payload;
      const filteredPayload = filterObject(rest);

      const config =
        payload.format === "json" && (page || per_page)
          ? {
              params: {
                ...(page && { page }),
                ...(per_page && { per_page }),
              },
            }
          : undefined;

      return axiosProtected.post(
        `${system}/reports/campaign-statement`,
        filteredPayload,
        config
      );
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message ||
          t("REPORT_GENERATION_FAILED") ||
          "Failed to generate report"
      );
    },
  });
};

export const useCampaignStatementReportQuery = (payload, enabled) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useQuery({
    queryKey: ["campaign-statement-report-export", payload],
    queryFn: async () => {
      const { orientation, ...rest } = payload;
      const filteredPayload = filterObject(rest);
      const extension = filteredPayload.format === "excel" ? "xlsx" : "pdf";
      const mimeType =
        filteredPayload.format === "excel"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf";

      const response = await axiosProtected.post(
        `${system}/reports/campaign-statement`,
        filteredPayload,
        {
          responseType: "blob",
          params:
            filteredPayload.format === "pdf"
              ? {
                  orientation: orientation || "landscape",
                }
              : undefined,
        }
      );

      const blob = new Blob([response.data], { type: mimeType });
      downloadFile(blob, `campaign-statement-report.${extension}`);

      return response;
    },
    enabled:
      enabled && (payload?.format === "pdf" || payload?.format === "excel"),
  });
};

// ******* Group Statement Reports *******

export const useGroupStatementReportMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useMutation({
    mutationFn: async (payload) => {
      const { page, per_page, ...rest } = payload;
      const filteredPayload = filterObject(rest);

      const config =
        payload.format === "json" && (page || per_page)
          ? {
              params: {
                ...(page && { page }),
                ...(per_page && { per_page }),
              },
            }
          : undefined;

      return axiosProtected.post(
        `${system}/reports/group-statement`,
        filteredPayload,
        config
      );
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message ||
          t("REPORT_GENERATION_FAILED") ||
          "Failed to generate report"
      );
    },
  });
};

export const useGroupStatementReportQuery = (payload, enabled) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useQuery({
    queryKey: ["group-statement-report-export", payload],
    queryFn: async () => {
      const { orientation, ...rest } = payload;
      const filteredPayload = filterObject(rest);
      const extension = filteredPayload.format === "excel" ? "xlsx" : "pdf";
      const mimeType =
        filteredPayload.format === "excel"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf";

      const response = await axiosProtected.post(
        `${system}/reports/group-statement`,
        filteredPayload,
        {
          responseType: "blob",
          params:
            filteredPayload.format === "pdf"
              ? {
                  orientation: orientation || "landscape",
                }
              : undefined,
        }
      );

      const blob = new Blob([response.data], { type: mimeType });
      downloadFile(blob, `group-statement-report.${extension}`);

      return response;
    },
    enabled:
      enabled && (payload?.format === "pdf" || payload?.format === "excel"),
  });
};

// ******* Housed Arrivals Service Center Distribution Report *******

export const useHousedArrivalsServiceCenterDistributionReportMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useMutation({
    mutationFn: async (payload) => {
      const { page, per_page, ...rest } = payload;
      const filteredPayload = filterObject(rest);

      const config =
        payload.format === "json" && (page || per_page)
          ? {
              params: filterObject({
                page,
                per_page,
              }),
            }
          : undefined;

      return axiosProtected.post(
        `${system}/reports/housed-arrivals-service-center-distribution`,
        filteredPayload,
        config
      );
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message ||
          t("REPORT_GENERATION_FAILED") ||
          "Failed to generate report"
      );
    },
  });
};

export const useHousedArrivalsServiceCenterDistributionReportQuery = (
  payload,
  enabled
) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useQuery({
    queryKey: ["housed-arrivals-service-center-distribution-report", payload],
    queryFn: async () => {
      const { orientation, ...rest } = payload;
      const filteredPayload = filterObject(rest);
      const formatMap = {
        pdf: "pdf",
        excel: "xlsx",
      };
      const extension = formatMap[filteredPayload.format] || "pdf";

      const response = await axiosProtected.post(
        `${system}/reports/housed-arrivals-service-center-distribution`,
        filteredPayload,
        {
          responseType: "blob",
          params:
            filteredPayload.format === "pdf"
              ? {
                  orientation: orientation || "landscape",
                }
              : undefined,
        }
      );

      const blob = new Blob([response.data], {
        type:
          extension === "pdf"
            ? "application/pdf"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      downloadFile(
        blob,
        `housed-arrivals-service-center-distribution-report.${extension}`
      );

      return response;
    },
    enabled:
      enabled && (payload?.format === "pdf" || payload?.format === "excel"),
  });
};



// ******* Departure Reports *******

export const useDepartureReportMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();

  return useMutation({
    mutationKey: ["departure-report"],
    mutationFn: (params) => axiosProtected.post("makkah/reports/departure-request", null, {
      responseType: params.format === "json" ? "json" : "blob",
      params,
    }),

    onSuccess: (response, params) => {
      if (params.format === "json") {
        return response
      }

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });
      const filename = `departure-report.pdf`;
      downloadFile(blob, filename);
      toast.success(t("REPORT_GENERATED_SUCCESSFULLY"));
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("REPORT_GENERATION_FAILED"));
    },
  });
};
export const useDepartureReporPreviewtQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  params = filterObject(params)
  return useQuery({
    queryKey: ["departure-report-preview", params],
    queryFn: () => axiosProtected.get("makkah/reports/departure-request/campaign", {
      params
    }),
    enabled: params.format === "json",
  });
};
export const useFlightsDetailsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  params = filterObject(params);
  return useQuery({
    queryKey: ["departure-report", params],
    queryFn: () => axiosProtected.get(`${currentSystem}/flight-details`, {
      params
    }),
  });
};

/**
 * Get a single flight detail by ID.
 * GET makkah/flight-details/:id
 */
export const useGetFlightDetailsByIdQuery = (id, options = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["flight-details", id],
    queryFn: () => axiosProtected.get(`${currentSystem}/flight-details/${id}`),
    enabled: Boolean(id),
    ...options,
  });
};

/**
 * Infinite query for flight-details airlines (searchable, paginated).
 * GET makkah/flight-details/airlines?search=...&pageSize=10&page=...
 */
export const useInfiniteFlightDetailsAirlinesQuery = ({ search, pageSize = 10 }) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const query = filterObject({ search, pageSize });

  return useInfiniteQuery({
    queryKey: ["infinite-flight-details-airlines", query],
    queryFn: ({ pageParam = 1 }) =>
      axiosProtected.get(`${currentSystem}/flight-details/airlines`, {
        params: {
          ...query,
          page: pageParam,
        },
      }),
    getNextPageParam: (lastPage) => {
      const meta = lastPage?.data?.meta;
      if (!meta) return undefined;
      const current = meta.current_page ?? 1;
      const last = meta.last_page ?? 1;
      return current < last ? current + 1 : undefined;
    },
    initialPageParam: 1,
  });
};

export const useCreateFlightDetailMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: (payload) => axiosProtected.post(`${currentSystem}/flight-details`, payload),
    mutationKey: "createFlightDetail",
    onSuccess: () => {
      toast.success(t("FLIGHT_CREATED_SUCCESSFULLY") || "Flight created successfully");
      queryClient.invalidateQueries({ queryKey: ["departure-report"] });
    },
  });
};

/**
 * Update a flight detail. PUT makkah/flight-details/update/:id
 */
export const useUpdateFlightDetailMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: ({ id, ...payload }) => axiosProtected.post(`${currentSystem}/flight-details/update/${id}`, payload),
    mutationKey: "updateFlightDetail",
    onSuccess: () => {
      toast.success(t("FLIGHT_UPDATED_SUCCESSFULLY") || "Flight updated successfully");
      queryClient.invalidateQueries({ queryKey: ["departure-report"] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("UPDATE_FLIGHT_FAILED") || "Failed to update flight");
    },
  });
};

/**
 * Delete a flight detail. DELETE /v1/makkah/flight-details/:id
 */
export const useDeleteFlightDetailMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: (id) => axiosProtected.delete(`${currentSystem}/flight-details/${id}`),
    mutationKey: "deleteFlightDetail",
    onSuccess: () => {
      toast.success(t("FLIGHT_DELETED_SUCCESSFULLY") || "Flight deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["departure-report"] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("DELETE_FLIGHT_FAILED") || "Failed to delete flight");
    },
  });
};

export const useInfiniteFlightsDetailsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const query = filterObject({
    per_page: 20,
    ...params,
  })
  return useInfiniteQuery({
    queryKey: ["infinite-private-transport-contracts", query],
    queryFn: ({ pageParam = 1 }) =>
      axiosProtected.get(`${currentSystem}/flight-details`, {
        params: {
          page: pageParam,
          ...query
        }
      }),
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = lastPage.data.meta;
      return current_page < last_page ? current_page + 1 : undefined;
    },
    initialPageParam: 1,
  });
};

export const usePrivateTransportContractsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  params = filterObject(params);
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["private-transport-contracts", params],
    queryFn: () => axiosProtected.get(`${currentSystem}/transport-contracts`, {
      params
    }),
  });
};

export const useTransportContractRoutesQuery = () => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["transport-contract-routes"],
    queryFn: () => axiosProtected.get(`${currentSystem}/transport-contracts/routes`),
  });
};

export const useInfinitePrivateTransportContractsQuery = ({ search }) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const query = filterObject({
    per_page: 20,
    search,
  })
  return useInfiniteQuery({
    queryKey: ["infinite-private-transport-contracts", search],
    queryFn: ({ pageParam = 1 }) =>
      axiosProtected.get(`${currentSystem}/transport-contracts`, {
        params: {
          page: pageParam,
          ...query
        }
      }),
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = lastPage.data.meta;
      return current_page < last_page ? current_page + 1 : undefined;
    },
    initialPageParam: 1,
  });
};


// =====================
// Pilgrim Reports
// =====================

export const useNotArrivedPilgrimsReportMutation = () => {
  const { axiosProtected } = useAxios();
  const { t, i18n } = useTranslation();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useMutation({
    mutationKey: ["not-arrived-pilgrims-report"],
    mutationFn: ({ params }) => axiosProtected.get(`${system}/reports/pilgrim-reports/not-coming`, {
      responseType: 'blob',
      params: filterObject({ ...params, lang: i18n.language === "ae" ? "ar" : "en" }),
    }),
    onSuccess: (response, variable) => {
      if (variable?.params?.format === "json") {
        return response
      }

      const blob = new Blob([response.data], {
        type: MIME_TYPES[variable?.params?.format],
      });
      const extension = variable?.params?.format === "pdf" ? "pdf" : "xlsx";
      const filename = `not-arrived-pilgrims-report.${extension}`;
      downloadFile(blob, filename);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("REPORT_GENERATION_FAILED"));
    },
  });
};
export const useNotArrivedPilgrimsReport = ({ params, enabled = false }) => {
  const { axiosProtected } = useAxios();
  const query = filterObject({ ...params });
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useQuery({
    queryKey: ["not-arrived-pilgrims-report", query],
    queryFn: () => axiosProtected.get(`${system}/reports/pilgrim-reports/not-coming`, {
      params: { ...query },
    })
    ,
    enabled,
  });
};
export const useNotAccommodatedPilgrimsReportMutation = () => {
  const { axiosProtected } = useAxios();
  const { t, i18n } = useTranslation();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useMutation({
    mutationKey: ["not-accommodated-pilgrims-report"],
    mutationFn: ({ params }) => axiosProtected.get(`${system}/reports/pilgrim-reports/not-accommodated`, {
      responseType: 'blob',
      params: filterObject({ ...params, lang: i18n.language === "ae" ? "ar" : "en" }),
    }),
    onSuccess: (response, variable) => {
      if (variable?.params?.format === "json") {
        return response
      }


      const blob = new Blob([response.data], {
        type: MIME_TYPES[variable?.params?.format],
      });
      const extension = variable?.params?.format === "pdf" ? "pdf" : "xlsx";
      const filename = `not-accommodated-pilgrims-report.${extension}`;
      downloadFile(blob, filename);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("REPORT_GENERATION_FAILED"));
    },
  });
};

export const useNotAccommodatedPilgrimsReport = ({ params, enabled = false }) => {
  const { axiosProtected } = useAxios();
  const query = filterObject({ ...params });
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useQuery({
    queryKey: ["not-accommodated-pilgrims-report", query],
    queryFn: () =>
      axiosProtected.get(`${system}/reports/pilgrim-reports/not-accommodated`, {
        params: { ...query },
      }),
    enabled,
  });
};

// =====================
// Incident Reports Report (Makkah)
// =====================

const incidentReportsReportBase = (system = "makkah") => {
  return `${system}/reports/incident-reports`;
};

const buildFormData = (payload) => {
  const formData = new FormData();
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value == null || value === "") return;
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v == null || v === "") return;
        formData.append(`${key}[]`, v);
      });
      return;
    }
    formData.append(key, value);
  });
  return formData;
};

export const useIncidentReportsReportMutation = () => {
  const { axiosProtected } = useAxios();
  const { t, i18n } = useTranslation();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationKey: ["incident-reports-report-export"],
    mutationFn: ({ params }) => {
      const filtered = filterObject({
        ...params,
        lang: i18n.language === "ae" ? "ar" : "en",
      });

      // Backend examples use multipart for file formats; keep JSON for json mode.
      const isJson = filtered?.format === "json";
      const data = isJson ? filtered : buildFormData(filtered);

      return axiosProtected.post(incidentReportsReportBase(currentSystem), data, {
        responseType: isJson ? "json" : "blob",
        ...(isJson ? {} : { headers: { "Content-Type": "multipart/form-data" } }),
      });
    },
    onSuccess: (response, variable) => {
      if (variable?.params?.format === "json") return response;

      const format = variable?.params?.format || "pdf";
      const blob = new Blob([response.data], { type: MIME_TYPES[format] });
      const extension = format === "pdf" ? "pdf" : "xlsx";
      downloadFile(blob, `incident-reports-report.${extension}`);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("REPORT_GENERATION_FAILED"));
    },
  });
};

export const useIncidentReportsReportJson = ({ params, enabled = false }) => {
  const { axiosProtected } = useAxios();
  const query = filterObject({ ...params, format: "json" });
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["incident-reports-report-json", query],
    queryFn: () => axiosProtected.post(incidentReportsReportBase(currentSystem), query, { responseType: "json" }),
    enabled,
  });
};

export const useCampaignGroupPilgrimsReportMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useMutation({
    mutationKey: ["campaign-group-pilgrims-report"],
    mutationFn: ({ params }) =>
      axiosProtected.post(`${system}/reports/campaign-group-pilgrims`, filterObject({
        ...params,
      }), {
        responseType: params.format === "json" ? "json" : "blob",
      }),
    onSuccess: (response, variable) => {
      if (variable?.params?.format === "json") {
        return response;
      }

      const blob = new Blob([response.data], {
        type: MIME_TYPES[variable?.params?.format],
      });
      const extension = variable?.params?.format === "pdf" ? "pdf" : "xlsx";
      const filename = `campaign-group-pilgrims-report.${extension}`;
      downloadFile(blob, filename);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("REPORT_GENERATION_FAILED"));
    },
  });
};

export const useCampaignGroupPilgrimsReport = ({ params, enabled = false }) => {
  const { axiosProtected } = useAxios();
  const query = filterObject({ ...params });

  return useQuery({
    queryKey: ["campaign-group-pilgrims-report", query],
    queryFn: () =>
      axiosProtected.post("makkah/reports/campaign-group-pilgrims", query, {
        responseType: "json",
      }),
    enabled,
  });
};

export const useHousingContractDetailReportMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useMutation({
    mutationKey: ["housing-contract-detail-report"],
    mutationFn: ({ params }) => {
      const filtered = filterObject(params);
      return axiosProtected.post(
        `${system}/reports/housing-contract-detail`,
        filtered,
        {
          responseType: params.format === "json" ? "json" : "blob",
        }
      );
    },
    onSuccess: (response, variable) => {
      if (variable?.params?.format === "json") {
        return response;
      }

      const blob = new Blob([response.data], {
        type: MIME_TYPES[variable?.params?.format],
      });
      const extension =
        variable?.params?.format === "pdf" ? "pdf" : "xlsx";
      const filename = `housing-contract-detail-report.${extension}`;
      downloadFile(blob, filename);
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || t("REPORT_GENERATION_FAILED")
      );
    },
  });
};

export const useHousingContractDetailReport = ({ params, enabled = false }) => {
  const { axiosProtected } = useAxios();
  const query = filterObject({ ...params });
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useQuery({
    queryKey: ["housing-contract-detail-report", query],
    queryFn: () =>
      axiosProtected.post(
        `${system}/reports/housing-contract-detail`,
        query,
        {
          responseType: "json",
        }
      ),
    enabled,
  });
};

export const useHouseContractCampaignsReportMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useMutation({
    mutationKey: ["house-contract-campaigns-report"],
    mutationFn: ({ params }) => {
      const filtered = filterObject(params);
      return axiosProtected.post(
        `${system}/reports/house-contract-campaigns`,
        filtered,
        {
          responseType: params.format === "json" ? "json" : "blob",
        }
      );
    },
    onSuccess: (response, variable) => {
      if (variable?.params?.format === "json") {
        return response;
      }

      const blob = new Blob([response.data], {
        type: MIME_TYPES[variable?.params?.format],
      });
      const extension =
        variable?.params?.format === "pdf" ? "pdf" : "xlsx";
      const filename = `house-contract-campaigns-report.${extension}`;
      downloadFile(blob, filename);
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || t("REPORT_GENERATION_FAILED")
      );
    },
  });
};

export const useHouseContractCampaignsReport = ({ params, enabled = false }) => {
  const { axiosProtected } = useAxios();
  const query = filterObject({ ...params });
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useQuery({
    queryKey: ["house-contract-campaigns-report", query],
    queryFn: () =>
      axiosProtected.post(
        `${system}/reports/house-contract-campaigns`,
        query,
        {
          responseType: "json",
        }
      ),
    enabled,
  });
};

export const useFoodContractsReportMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useMutation({
    mutationKey: ["food-contracts-report"],
    mutationFn: ({ params }) => {
      const filtered = filterObject(params);
      return axiosProtected.post(
        `${system}/reports/food-contracts`,
        filtered,
        {
          responseType: params.format === "json" ? "json" : "blob",
        }
      );
    },
    onSuccess: (response, variable) => {
      if (variable?.params?.format === "json") {
        return response;
      }

      const blob = new Blob([response.data], {
        type: MIME_TYPES[variable?.params?.format],
      });
      const extension =
        variable?.params?.format === "pdf" ? "pdf" : "xlsx";
      const filename = `food-contracts-report.${extension}`;
      downloadFile(blob, filename);
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || t("REPORT_GENERATION_FAILED")
      );
    },
  });
};

export const useFoodContractsReport = ({ params, enabled = false }) => {
  const { axiosProtected } = useAxios();
  const query = filterObject({ ...params });
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useQuery({
    queryKey: ["food-contracts-report", query],
    queryFn: () =>
      axiosProtected.post(
        `${system}/reports/food-contracts`,
        query,
        {
          responseType: "json",
        }
      ),
    enabled,
  });
};

export const useHousingComprehensiveDetailReportMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useMutation({
    mutationKey: ["housing-comprehensive-detail-report"],
    mutationFn: ({ params }) => {
      const filtered = filterObject(params);
      return axiosProtected.post(
        `${system}/reports/housing-comprehensive-detail`,
        filtered,
        {
          responseType: params.format === "json" ? "json" : "blob",
        }
      );
    },
    onSuccess: (response, variable) => {
      if (variable?.params?.format === "json") {
        return response;
      }

      const blob = new Blob([response.data], {
        type: MIME_TYPES[variable?.params?.format],
      });
      const extension =
        variable?.params?.format === "pdf" ? "pdf" : "xlsx";
      const filename = `housing-comprehensive-detail-report.${extension}`;
      downloadFile(blob, filename);
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || t("REPORT_GENERATION_FAILED")
      );
    },
  });
};

export const useHousingComprehensiveDetailReport = ({
  params,
  enabled = false,
}) => {
  const { axiosProtected } = useAxios();
  const query = filterObject({ ...params });
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useQuery({
    queryKey: ["housing-comprehensive-detail-report", query],
    queryFn: () =>
      axiosProtected.post(
        `${system}/reports/housing-comprehensive-detail`,
        query,
        {
          responseType: "json",
        }
      ),
    enabled,
  });
};

export const useHousingBuildingDelegateDetailReportMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useMutation({
    mutationKey: ["housing-building-delegate-detail-report"],
    mutationFn: ({ params }) => {
      const filtered = filterObject(params);
      return axiosProtected.post(
        `${system}/reports/housing-building-delegate-detail`,
        filtered,
        {
          responseType: params.format === "json" ? "json" : "blob",
        }
      );
    },
    onSuccess: (response, variable) => {
      if (variable?.params?.format === "json") {
        return response;
      }

      const blob = new Blob([response.data], {
        type: MIME_TYPES[variable?.params?.format],
      });
      const extension =
        variable?.params?.format === "pdf" ? "pdf" : "xlsx";
      const filename = `housing-building-delegate-detail-report.${extension}`;
      downloadFile(blob, filename);
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || t("REPORT_GENERATION_FAILED")
      );
    },
  });
};

export const useHousingBuildingDelegateDetailReport = ({
  params,
  enabled = false,
}) => {
  const { axiosProtected } = useAxios();
  const query = filterObject({ ...params });
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem;

  return useQuery({
    queryKey: ["housing-building-delegate-detail-report", query],
    queryFn: () =>
      axiosProtected.post(
        `${system}/reports/housing-building-delegate-detail`,
        query,
        {
          responseType: "json",
        }
      ),
    enabled,
  });
};

// Makkah-only: pilgrims arrivals / departures by nationality (port buckets + incidents)
const PILGRIM_NATIONALITY_STATS_PATH =
  "makkah/reports/pilgrim-reports/nationality-stats";

export const usePilgrimNationalityStatsReportMutation = () => {
  const { axiosProtected } = useAxios();
  const { t, i18n } = useTranslation();

  return useMutation({
    mutationKey: ["pilgrim-nationality-stats-report"],
    mutationFn: ({ params }) => {
      const filtered = filterObject({
        ...params,
        lang: i18n.language === "ae" ? "ar" : "en",
      });
      const fmt = filtered?.format;
      const isJson = fmt === "json";
      return axiosProtected.get(PILGRIM_NATIONALITY_STATS_PATH, {
        params: filtered,
        responseType: isJson ? "json" : "blob",
      });
    },
    onSuccess: (response, variable) => {
      if (variable?.params?.format === "json") {
        return response;
      }

      const fmt = variable?.params?.format || "pdf";
      const mimeKey = fmt === "pdf" ? "pdf" : "excel";
      const blob = new Blob([response.data], {
        type: MIME_TYPES[mimeKey],
      });
      const extension = fmt === "pdf" ? "pdf" : "xlsx";
      const filename = `pilgrim_nationality_stats_report.${extension}`;
      downloadFile(blob, filename);
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || t("REPORT_GENERATION_FAILED")
      );
    },
  });
};

export const usePilgrimNationalityStatsReport = ({ params, enabled = false }) => {
  const { axiosProtected } = useAxios();
  const { i18n } = useTranslation();
  const query = filterObject({
    ...params,
    lang: i18n.language === "ae" ? "ar" : "en",
  });

  return useQuery({
    queryKey: ["pilgrim-nationality-stats-report", query],
    queryFn: () =>
      axiosProtected.get(PILGRIM_NATIONALITY_STATS_PATH, {
        params: query,
        responseType: "json",
      }),
    enabled,
  });
};

// Makkah-only: pilgrims arrivals / departures by mission (port buckets + incidents)
const PILGRIM_MISSION_STATS_PATH =
  "makkah/reports/pilgrim-reports/mission-stats";

export const usePilgrimMissionStatsReportMutation = () => {
  const { axiosProtected } = useAxios();
  const { t, i18n } = useTranslation();

  return useMutation({
    mutationKey: ["pilgrim-mission-stats-report"],
    mutationFn: ({ params }) => {
      const filtered = filterObject({
        ...params,
        lang: i18n.language === "ae" ? "ar" : "en",
      });
      const fmt = filtered?.format;
      const isJson = fmt === "json";
      return axiosProtected.get(PILGRIM_MISSION_STATS_PATH, {
        params: filtered,
        responseType: isJson ? "json" : "blob",
      });
    },
    onSuccess: (response, variable) => {
      if (variable?.params?.format === "json") {
        return response;
      }

      const fmt = variable?.params?.format || "pdf";
      const mimeKey = fmt === "pdf" ? "pdf" : "excel";
      const blob = new Blob([response.data], {
        type: MIME_TYPES[mimeKey],
      });
      const extension = fmt === "pdf" ? "pdf" : "xlsx";
      const filename = `pilgrim_mission_stats_report.${extension}`;
      downloadFile(blob, filename);
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || t("REPORT_GENERATION_FAILED")
      );
    },
  });
};

export const usePilgrimMissionStatsReport = ({ params, enabled = false }) => {
  const { axiosProtected } = useAxios();
  const { i18n } = useTranslation();
  const query = filterObject({
    ...params,
    lang: i18n.language === "ae" ? "ar" : "en",
  });

  return useQuery({
    queryKey: ["pilgrim-mission-stats-report", query],
    queryFn: () =>
      axiosProtected.get(PILGRIM_MISSION_STATS_PATH, {
        params: query,
        responseType: "json",
      }),
    enabled,
  });
};

